import * as React from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    MenuItem,
    Chip,
    Autocomplete,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import { DEFAULT_BOOKING_STATUSES, safeStr } from "./bookingTypes";

// ✅ TRY import (may be undefined if module mismatch / wrong file / circular deps)
import * as AdminFetchMod from "../../../services/adminFetch";

type CustomerOpt = { id: number; full_name?: string | null; email?: string | null; phone?: string | null };
type PropertyOpt = { id: number; display_title?: string | null; title?: string | null; slug?: string | null; city?: string | null };

function useDebounced<T>(value: T, ms: number) {
    const [v, setV] = React.useState(value);
    React.useEffect(() => {
        const t = window.setTimeout(() => setV(value), ms);
        return () => window.clearTimeout(t);
    }, [value, ms]);
    return v;
}

// ✅ Fallback adminFetch (if imported one is undefined at runtime)
function adminFetchLocal(path: string, init: RequestInit = {}) {
    const raw = process.env.REACT_APP_API_BASE || "https://www.ritasproperties.com";
    const API_BASE = raw.replace(/\/+$/, "");
    const url = `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

    const headers = new Headers(init.headers || {});
    headers.set("Accept", "application/json");

    // eslint-disable-next-line no-console
    console.log("[adminFetchLocal] ->", init.method || "GET", url);

    return fetch(url, { ...init, headers, credentials: "include" });
}

export default function AdminBookingCreatePage() {
    const { locale } = useParams<{ locale: string }>();
    const nav = useNavigate();

    // ---------------------------
    // Debug state (visible panel)
    // ---------------------------
    const [dbg, setDbg] = React.useState<string[]>([]);
    const pushDbg = React.useCallback((msg: string) => {
        setDbg((prev) => [`${new Date().toISOString()}  ${msg}`, ...prev].slice(0, 60));
    }, []);

    // ✅ Resolve adminFetch safely (imported OR fallback)
    const adminFetch =
        (AdminFetchMod as any)?.adminFetch && typeof (AdminFetchMod as any).adminFetch === "function"
            ? ((AdminFetchMod as any).adminFetch as (path: string, init?: RequestInit) => Promise<Response>)
            : adminFetchLocal;

    // 🔥 hard debug on mount
    React.useEffect(() => {
        // eslint-disable-next-line no-console
        console.log("[DEBUG] AdminBookingCreatePage mounted");
        // eslint-disable-next-line no-console
        console.log("[DEBUG] AdminFetchMod =", AdminFetchMod);
        // eslint-disable-next-line no-console
        console.log("[DEBUG] resolved adminFetch =", adminFetch);

        pushDbg(`Mounted component. locale=${locale || "el"}`);
        pushDbg(`AdminFetchMod keys=${Object.keys(AdminFetchMod as any).join(",") || "—"}`);
        pushDbg(`Resolved adminFetch=${adminFetch === adminFetchLocal ? "LOCAL_FALLBACK" : "IMPORTED_OK"}`);
    }, [adminFetch, locale, pushDbg]);

    // ---------------------------
    // Form state
    // ---------------------------
    const [property, setProperty] = React.useState<PropertyOpt | null>(null);
    const [customer, setCustomer] = React.useState<CustomerOpt | null>(null);

    const [checkIn, setCheckIn] = React.useState<string>("");
    const [checkOut, setCheckOut] = React.useState<string>("");
    const [guests, setGuests] = React.useState<string>("2");
    const [status, setStatus] = React.useState<string>("confirmed");
    const [notes, setNotes] = React.useState<string>("");

    // live inputs
    const [propertyInput, setPropertyInput] = React.useState("");
    const [customerInput, setCustomerInput] = React.useState("");

    const propertyQ = useDebounced(propertyInput.trim(), 250);
    const customerQ = useDebounced(customerInput.trim(), 250);

    // options
    const [propLoading, setPropLoading] = React.useState(false);
    const [custLoading, setCustLoading] = React.useState(false);

    const [propOptions, setPropOptions] = React.useState<PropertyOpt[]>([]);
    const [custOptions, setCustOptions] = React.useState<CustomerOpt[]>([]);

    const [saving, setSaving] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);

    const [toast, setToast] = React.useState<{ open: boolean; msg: string; severity: "success" | "error" }>({
        open: false,
        msg: "",
        severity: "success",
    });

    // new customer modal
    const [newCustOpen, setNewCustOpen] = React.useState(false);
    const [newCustName, setNewCustName] = React.useState("");
    const [newCustEmail, setNewCustEmail] = React.useState("");
    const [newCustPhone, setNewCustPhone] = React.useState("");
    const [newCustSaving, setNewCustSaving] = React.useState(false);

    // ---------------------------
    // Helper: fetch wrapper w/ logs
    // ---------------------------
    const fetchJson = React.useCallback(
        async (url: string) => {
            pushDbg(`REQ GET ${url}`);
            const res = await adminFetch(url, { method: "GET" });
            pushDbg(`RES ${res.status} ${url}`);
            const txt = await res.text();
            let j: any = {};
            try {
                j = txt ? JSON.parse(txt) : {};
            } catch {
                j = { _raw: txt };
            }
            if (!res.ok) {
                const msg = j?.error || j?.message || `Request failed (${res.status})`;
                pushDbg(`ERR ${msg}`);
                throw new Error(msg);
            }
            return j;
        },
        [adminFetch, pushDbg]
    );

    // ---------------------------
    // Properties: preload list + live search
    // ---------------------------
    React.useEffect(() => {
        let alive = true;

        const run = async () => {
            setPropLoading(true);
            try {
                const params = new URLSearchParams();
                // ✅ preload: if <2 chars, no q => backend returns default list
                if (propertyQ && propertyQ.length >= 2) params.set("q", propertyQ);
                params.set("limit", "50");
                params.set("offset", "0");

                const url = `/api/admin/properties?${params.toString()}`;
                const j = await fetchJson(url);

                const items = Array.isArray(j?.items) ? (j.items as PropertyOpt[]) : [];
                if (!alive) return;
                setPropOptions(items);
                pushDbg(`Properties loaded: ${items.length} items (q="${propertyQ || ""}")`);
            } catch (e: any) {
                if (!alive) return;
                setPropOptions([]);
                pushDbg(`Properties failed: ${e?.message || "unknown"}`);
            } finally {
                if (alive) setPropLoading(false);
            }
        };

        run();
        return () => {
            alive = false;
        };
    }, [propertyQ, fetchJson, pushDbg]);

    // ---------------------------
    // Customers: preload list + live search
    // ---------------------------
    React.useEffect(() => {
        let alive = true;

        const run = async () => {
            setCustLoading(true);
            try {
                const params = new URLSearchParams();
                // ✅ preload: if <2 chars, no q => backend returns default list
                if (customerQ && customerQ.length >= 2) params.set("q", customerQ);
                params.set("limit", "50");
                params.set("offset", "0");

                const url = `/api/admin/customers?${params.toString()}`;
                const j = await fetchJson(url);

                const items = Array.isArray(j?.items) ? (j.items as CustomerOpt[]) : [];
                if (!alive) return;
                setCustOptions(items);
                pushDbg(`Customers loaded: ${items.length} items (q="${customerQ || ""}")`);
            } catch (e: any) {
                if (!alive) return;
                setCustOptions([]);
                pushDbg(`Customers failed: ${e?.message || "unknown"}`);
            } finally {
                if (alive) setCustLoading(false);
            }
        };

        run();
        return () => {
            alive = false;
        };
    }, [customerQ, fetchJson, pushDbg]);

    const openNewCustomer = () => {
        setNewCustName(customerInput.trim());
        setNewCustEmail("");
        setNewCustPhone("");
        setNewCustOpen(true);
    };

    const createCustomer = async () => {
        const full_name = newCustName.trim();
        if (!full_name) {
            setToast({ open: true, msg: "Customer name is required.", severity: "error" });
            return;
        }

        setNewCustSaving(true);
        try {
            const payload = {
                full_name,
                email: newCustEmail.trim() || null,
                phone: newCustPhone.trim() || null,
            };

            pushDbg(`REQ POST /api/admin/customers payload=${JSON.stringify(payload)}`);
            const res = await adminFetch(`/api/admin/customers`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            pushDbg(`RES ${res.status} POST /api/admin/customers`);

            const j = (await res.json().catch(() => ({}))) as any;
            if (!res.ok) throw new Error(j?.error || `Create failed (${res.status})`);

            const created = (j?.item ?? j) as CustomerOpt;
            setCustomer(created);
            setCustomerInput(safeStr(created?.full_name, ""));
            setNewCustOpen(false);
            setToast({ open: true, msg: "Customer created.", severity: "success" });
            pushDbg(`Customer created id=${created?.id}`);
        } catch (e: any) {
            setToast({ open: true, msg: e?.message || "Failed to create customer.", severity: "error" });
            pushDbg(`Customer create failed: ${e?.message || "unknown"}`);
        } finally {
            setNewCustSaving(false);
        }
    };

    const saveBooking = async () => {
        setErr(null);

        if (!property?.id) {
            setErr("Please select a property.");
            return;
        }
        if (!checkIn || !checkOut) {
            setErr("Please select check-in and check-out dates.");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                property_id: property.id,
                customer_id: customer?.id || null,
                check_in: checkIn,
                check_out: checkOut,
                guests_total: guests ? Number(guests) : null,
                status: status || "confirmed",
                internal_notes: notes || null,
            };

            pushDbg(`REQ POST /api/admin/bookings payload=${JSON.stringify(payload)}`);
            const res = await adminFetch(`/api/admin/bookings`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            pushDbg(`RES ${res.status} POST /api/admin/bookings`);

            const j = (await res.json().catch(() => ({}))) as any;
            if (!res.ok) throw new Error(j?.error || `Save failed (${res.status})`);

            const created = j?.item ?? j;
            const newId = created?.id;

            setToast({ open: true, msg: "Booking created.", severity: "success" });
            pushDbg(`Booking created id=${newId || "—"}`);

            if (newId) nav(`/${locale || "el"}/admin/bookings/${newId}`, { replace: true });
            else nav(`/${locale || "el"}/admin/bookings`, { replace: true });
        } catch (e: any) {
            setErr(e?.message || "Failed to create booking.");
            pushDbg(`Booking create failed: ${e?.message || "unknown"}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    borderColor: "divider",
                    overflow: "hidden",
                    background:
                        "radial-gradient(900px 500px at 15% -20%, rgba(46,125,50,0.12), transparent 60%), radial-gradient(900px 500px at 85% -10%, rgba(25,118,210,0.10), transparent 60%)",
                }}
            >
                <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} sx={{ p: 2, gap: 1.5 }}>
                    <Stack spacing={0.25}>
                        <Typography variant="h6" sx={{ fontWeight: 950 }}>
                            New booking
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create a manual booking with property + customer + dates.
                        </Typography>
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => nav(-1)}
                            sx={{ borderRadius: 2, fontWeight: 900 }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            onClick={saveBooking}
                            disabled={saving}
                            sx={{ borderRadius: 2, fontWeight: 950 }}
                        >
                            Create
                        </Button>
                    </Stack>
                </Stack>

                <Divider />

                <Box sx={{ p: 2 }}>
                    {err ? <Alert severity="error">{err}</Alert> : null}

                    <Stack spacing={2}>
                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                            <Stack spacing={1.5}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                    Property & Customer
                                </Typography>
                                <Divider />

                                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                    <Autocomplete
                                        value={property}
                                        onChange={(_, v) => {
                                            pushDbg(`UI property selected id=${v?.id || "—"}`);
                                            setProperty(v);
                                        }}
                                        inputValue={propertyInput}
                                        onInputChange={(_, v, reason) => {
                                            pushDbg(`UI property input="${v}" reason=${reason}`);
                                            setPropertyInput(v);
                                        }}
                                        options={propOptions}
                                        loading={propLoading}
                                        getOptionLabel={(o) => {
                                            const t = safeStr(o?.display_title, "");
                                            return t ? t : `Property #${o?.id ?? "—"}`;
                                        }}
                                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Property"
                                                placeholder="Start typing… (shows list even when empty)"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {propLoading ? <CircularProgress size={16} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderOption={(props, o) => (
                                            <li {...props} key={o.id}>
                                                <Stack>
                                                    <Typography sx={{ fontWeight: 850 }}>{safeStr(o.display_title, `Property #${o.id}`)}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        #{o.id}
                                                        {o.slug ? ` • /${o.slug}` : ""}
                                                        {o.city ? ` • ${o.city}` : ""}
                                                    </Typography>
                                                </Stack>
                                            </li>
                                        )}
                                        fullWidth
                                    />

                                    <Stack spacing={1} sx={{ flex: 1 }}>
                                        <Autocomplete
                                            value={customer}
                                            onChange={(_, v) => {
                                                pushDbg(`UI customer selected id=${v?.id || "—"}`);
                                                setCustomer(v);
                                            }}
                                            inputValue={customerInput}
                                            onInputChange={(_, v, reason) => {
                                                pushDbg(`UI customer input="${v}" reason=${reason}`);
                                                setCustomerInput(v);
                                            }}
                                            options={custOptions}
                                            loading={custLoading}
                                            getOptionLabel={(o) => safeStr(o?.full_name, "")}
                                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Customer"
                                                    placeholder="Start typing… (shows list even when empty)"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {custLoading ? <CircularProgress size={16} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, o) => (
                                                <li {...props} key={o.id}>
                                                    <Stack>
                                                        <Typography sx={{ fontWeight: 850 }}>{safeStr(o.full_name, "—")}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {o.email ? o.email : "—"}
                                                            {o.phone ? ` • ${o.phone}` : ""}
                                                        </Typography>
                                                    </Stack>
                                                </li>
                                            )}
                                            fullWidth
                                        />

                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Button onClick={openNewCustomer} startIcon={<AddIcon />} variant="outlined" sx={{ borderRadius: 2, fontWeight: 900 }}>
                                                New customer
                                            </Button>

                                            {customer ? (
                                                <Chip
                                                    label={`Selected: ${safeStr(customer.full_name, "Customer")}`}
                                                    variant="outlined"
                                                    sx={{ borderRadius: 999, fontWeight: 800 }}
                                                />
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    If the customer doesn’t exist, create one.
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                            <Stack spacing={1.5}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                    Stay details
                                </Typography>
                                <Divider />

                                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                    <TextField
                                        label="Check-in"
                                        type="date"
                                        value={checkIn}
                                        onChange={(e) => setCheckIn(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ minWidth: 200 }}
                                    />
                                    <TextField
                                        label="Check-out"
                                        type="date"
                                        value={checkOut}
                                        onChange={(e) => setCheckOut(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ minWidth: 200 }}
                                    />
                                    <TextField label="Guests" value={guests} onChange={(e) => setGuests(e.target.value)} sx={{ minWidth: 140 }} />
                                    <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 220 }}>
                                        {DEFAULT_BOOKING_STATUSES.filter((x) => x.value !== "").map((o) => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>

                                <TextField
                                    label="Internal notes"
                                    multiline
                                    minRows={4}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Optional notes for admins…"
                                />
                            </Stack>
                        </Paper>

                        {/* ✅ Visible debug panel */}
                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                            <Stack spacing={1}>
                                <Typography sx={{ fontWeight: 950 }}>Debug</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Αν αυτό γράφει logs, τότε το component φορτώνει και τα effects τρέχουν.
                                </Typography>
                                <Divider />
                                <Box
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        p: 1,
                                        borderRadius: 2,
                                        bgcolor: "background.default",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        maxHeight: 240,
                                        overflow: "auto",
                                        fontSize: 12,
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {dbg.length ? dbg.join("\n") : "—"}
                                </Box>
                            </Stack>
                        </Paper>
                    </Stack>
                </Box>
            </Paper>

            {/* New customer modal */}
            <Dialog open={newCustOpen} onClose={() => setNewCustOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 950 }}>Create new customer</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField label="Full name" value={newCustName} onChange={(e) => setNewCustName(e.target.value)} autoFocus />
                        <TextField label="Email (optional)" value={newCustEmail} onChange={(e) => setNewCustEmail(e.target.value)} />
                        <TextField label="Phone (optional)" value={newCustPhone} onChange={(e) => setNewCustPhone(e.target.value)} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setNewCustOpen(false)} variant="text" sx={{ fontWeight: 800 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={createCustomer}
                        variant="contained"
                        startIcon={newCustSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                        disabled={newCustSaving}
                        sx={{ fontWeight: 950, borderRadius: 2 }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={toast.severity} variant="filled" onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ fontWeight: 800 }}>
                    {toast.msg}
                </Alert>
            </Snackbar>
        </Stack>
    );
}
